<template>
  <div
    class="flex gap-3 px-4 py-2 items-start select-none"
    data-test="appointment-statistic-row"
  >
    <button
      type="button"
      class="shrink-0 cursor-pointer rounded-full"
      data-test="appointment-statistic-row-avatar"
      @click="onSelectParticipant"
    >
      <contact-avatar
        :size="40"
        :contact="contact"
        :name="avatarName"
        :icon="data.isTemporary ? 'userM' : undefined"
      />
    </button>
    <div class="flex flex-col gap-0.5 min-w-0 grow">
      <div class="flex items-start justify-between gap-2">
        <p
          class="break-words min-w-0 cursor-pointer text-body-m-14-20 text-fg-base-fg10 hover:text-surf-brand-basedarkblue-def"
          data-test="appointment-statistic-row-name"
          @click="onSelectParticipant"
        >
          {{ displayName }}
        </p>
        <p class="text-body-r-14-20 text-fg-base-fg10 shrink-0">
          {{ getTime(data.timestamp) }}
        </p>
      </div>
      <div
        v-if="actionText"
        class="flex items-start gap-1 min-w-0"
        data-test="text"
      >
        <wui-icon
          v-if="actionIcon"
          :name="actionIcon"
          class="w-[18px]! h-[18px]! shrink-0 text-fg-base-fg22 mt-0.5"
        />
        <p class="text-body-r-14-20 text-fg-base-fg22 break-words min-w-0">
          {{ actionText }}
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { WuiIcon } from '@wui/common-library'
import { computed } from 'vue'

import { ContactAvatar } from '@/entities/contact-avatar'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { getUnknownContactLabel } from '@/shared/utils/contacts'
import { isGuest } from '@/shared/utils/uri'

import {
  getContact,
  CallStatistic,
  getTime,
  getCallStatisticActionText,
  getCallStatisticStatusIcon,
} from '..'

const props = defineProps<{
  data: CallStatistic
}>()

const emit = defineEmits<{
  (event: 'select-participant', payload: { name: string, pServed?: string }): void
}>()

const { currentUser } = useCurrentUser()
const { t } = useLocalization()

const contact = computed(() => {
  if (props.data.participantPServed === currentUser.value?.imLogin && currentUser.value) {
    return currentUser.value
  } else if (props.data.participantPServed) {
    return getContact(props.data.participantPServed)
  }
  return undefined
})

const name = computed(() => {
  if (props.data.participantName) {
    return props.data.participantName
  }

  return props.data.participantPServed
    ? getUnknownContactLabel(props.data.participantPServed)
    : t('common.externalContact')
})

const displayName = computed(() => {
  const isGuestParticipant = Boolean(
    props.data.participantPServed && isGuest(props.data.participantPServed),
  )

  if (props.data.isTemporary || isGuestParticipant) {
    return `${name.value} (${t('common.guest').toLowerCase()})`
  }
  return name.value
})

const avatarName = computed(() => (contact.value ? undefined : name.value))

const actionText = computed(() => getCallStatisticActionText(props.data))
const actionIcon = computed(() => getCallStatisticStatusIcon(props.data))

const onSelectParticipant = () => {
  emit('select-participant', {
    name: name.value,
    ...(props.data.participantPServed
      ? { pServed: props.data.participantPServed }
      : {}),
  })
}
</script>
